import { computed, toValue } from 'vue';
import { useCarBookingStore } from 'dashboard/stores/carBooking';
import { isoAt } from 'dashboard/components/carBooking/dateTime';

// "0800" -> "08:00"; null/empty -> null.
const hhmmToTime = s => (s ? `${s.slice(0, 2)}:${s.slice(2, 4)}` : null);

// Encapsulates the station-hours gating logic shared by the Trip search form
// and the Offers edit bar. `conversationId` may be a ref or a plain value.
export function useStationSchedule(conversationId) {
  const carBooking = useCarBookingStore();

  const session = computed(() =>
    carBooking.sessionFor(toValue(conversationId))
  );
  const form = computed(() => session.value.form);

  // All schedule slots for a station on a "YYYY-MM-DD" date: the matching
  // dayNumber, filtered to those whose seasonal validity covers the date. A day
  // can have several slots (split open hours + separate after-hours slots).
  // Exception entries (schedType 'E', e.g. a holiday closure) override the
  // general weekly ones for that date.
  const scheduleSlotsFor = (code, dateStr) => {
    const entries = session.value.schedules[code];
    if (!entries || !dateStr) return [];
    const dayNumber = new Date(`${dateStr}T00:00:00`).getDay() + 1;
    const ymd = dateStr.replaceAll('-', '');
    const valid = entries.filter(
      e =>
        e.dayNumber === dayNumber &&
        (!e.validFrom || e.validFrom <= ymd) &&
        (!e.validTo || e.validTo >= ymd)
    );
    const exceptions = valid.filter(e => e.schedType === 'E');
    return exceptions.length ? exceptions : valid;
  };

  // Union span [earliest open, latest close] across the given slots.
  const spanOf = slots => {
    const opens = slots
      .map(s => s.opens)
      .filter(Boolean)
      .sort();
    const closes = slots
      .map(s => s.closes)
      .filter(Boolean)
      .sort();
    if (!opens.length || !closes.length)
      return { minTime: null, maxTime: null };
    return {
      minTime: hhmmToTime(opens[0]),
      maxTime: hhmmToTime(closes[closes.length - 1]),
    };
  };

  // Resolves TimePicker constraints for a station/date. Pickup is limited to the
  // counter's open slots (afterHours !== 'Y'); dropoff additionally includes
  // bookable after-hours slots (status 'F' key-drop or 'R' on-request). The
  // window is the union span of those slots — after-hours is NOT unbounded, it
  // only covers the slots the station actually staffs. {} => schedule unknown.
  const availabilityFor = (code, dateStr, mode) => {
    const slots = scheduleSlotsFor(code, dateStr);
    if (!slots.length) return {};
    const open = slots.filter(s => s.afterHours !== 'Y' && s.opens && s.closes);
    const afterHours = slots.filter(
      s =>
        s.afterHours === 'Y' &&
        (s.afterHoursStatus === 'F' || s.afterHoursStatus === 'R')
    );
    const usable = mode === 'dropoff' ? [...open, ...afterHours] : open;
    const { minTime, maxTime } = spanOf(usable);
    let ahStatus = null;
    if (afterHours.some(s => s.afterHoursStatus === 'R')) ahStatus = 'R';
    else if (afterHours.length) ahStatus = 'F';
    return {
      minTime,
      maxTime,
      allowOutside: false,
      closed: !minTime || !maxTime,
      afterHoursStatus: mode === 'dropoff' ? ahStatus : null,
    };
  };

  const pickupAvailability = computed(() =>
    availabilityFor(
      form.value.pickupLocationCode,
      form.value.pickupDate,
      'pickup'
    )
  );
  const dropoffAvailability = computed(() =>
    availabilityFor(
      form.value.dropoffLocationCode,
      form.value.dropoffDate,
      'dropoff'
    )
  );

  // Rental length in whole days from the local "YYYY-MM-DD" pickup/dropoff
  // dates (clamped ≥ 1). Returns 0 — and the pill is hidden — until both are set.
  const rentalDays = computed(() => {
    const { pickupDate, dropoffDate } = form.value;
    if (!pickupDate || !dropoffDate) return 0;
    const start = new Date(`${pickupDate}T00:00:00`);
    const end = new Date(`${dropoffDate}T00:00:00`);
    const diff = Math.round((end - start) / 86400000);
    return diff > 0 ? diff : 1;
  });

  return {
    hhmmToTime,
    scheduleSlotsFor,
    spanOf,
    availabilityFor,
    pickupAvailability,
    dropoffAvailability,
    rentalDays,
    isoAt,
  };
}
